import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { IHSRCharacter } from '../../../common/model/graphql-types';
import { Alert } from 'react-bootstrap';
import { HSRPath } from './hsr-path';
import { HSRElement } from './hsr-element';
import { HSRRarity } from './hsr-rarity';
import { HSRRatings } from '../../data/hsr-ratings';
import { RatingBox } from '../../../common/components/rating-box';
import {
  faHandFist,
  faVirus,
  faFlask,
  faBriefcaseMedical
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IProps {
  character: IHSRCharacter;
}

export const HSRCharacterPopover: React.FC<IProps> = ({ character }) => {
  const image = character.smallImage
    ? getImage(character.smallImage.localFile.childImageSharp)
    : null;

  const charInfo = HSRRatings.filter((char) => char.slug === character.slug);
  return (
    <div className="character-popover hsr">
      <div className="top hsr">
        <div className={`avatar hsr rarity-${character.rarity}`}>
          {image && (
            <GatsbyImage
              className="disable-transition"
              image={image}
              alt={`${character.name}`}
            />
          )}
        </div>
        <p className="name">
          <span className="emp-name">{character.name}</span>{' '}
          {character.isNew && <span className="tag new">NEW!</span>}{' '}
        </p>
        <div className="type-class">
          <HSRRarity charClass={character.rarity} />
          <HSRElement charClass={character.element} />
          <HSRPath charClass={character.path} />
        </div>
      </div>
      {!character.isReviewPending ? (
        <>
          {charInfo.length > 1 && (
            <p className="hsr-role">
              This character can be played in multiple roles, so there's more
              than one row of ratings visible below.
            </p>
          )}
          {charInfo.map((charRating, idx) => {
            return (
              <div key={idx}>
                {charRating.rarity === 5 ||
                charRating.slug === 'march-7th-swordmaster' ? (
                  <>
                    {charRating.slug === 'trailblazer-destruction' ||
                    charRating.slug === 'trailblazer-harmony' ||
                    charRating.slug === 'march-7th-swordmaster' ||
                    charRating.slug === 'trailblazer-preservation' ||
                    charRating.slug === 'trailblazer-remembrance' ? (
                      <div className="bottom rev three with-margin">
                        {charRating.slug === 'trailblazer-remembrance' ? (
                          <>
                            <RatingBox
                              game="hsr"
                              rating={charRating.moc_rating}
                              ratingName="MoC (E0)"
                            />
                            <RatingBox
                              game="hsr"
                              rating={charRating.pure_rating}
                              ratingName="PF (E0)"
                            />
                            <RatingBox
                              game="hsr"
                              rating={charRating.apo_rating}
                              ratingName="AS (E0)"
                            />
                          </>
                        ) : (
                          <>
                            <RatingBox
                              game="hsr"
                              rating={charRating.moc_rating}
                              ratingName="MoC (E6)"
                            />
                            <RatingBox
                              game="hsr"
                              rating={charRating.pure_rating}
                              ratingName="PF (E6)"
                            />
                            <RatingBox
                              game="hsr"
                              rating={charRating.apo_rating}
                              ratingName="AS (E6)"
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        {charInfo.length > 1 ? (
                          <>
                            {charRating.cat === 'DPS' && (
                              <h6 className="hsr-small-role dps">
                                <FontAwesomeIcon icon={faHandFist} width="12" />{' '}
                                DPS
                              </h6>
                            )}
                            {charRating.cat === 'Specialist' && (
                              <h6 className="hsr-small-role debuffer">
                                <FontAwesomeIcon icon={faVirus} width="12" />{' '}
                                Support DPS
                              </h6>
                            )}
                            {charRating.cat === 'Amplifier' && (
                              <h6 className="hsr-small-role support">
                                <FontAwesomeIcon icon={faFlask} width="12" />{' '}
                                Amplifier
                              </h6>
                            )}
                            {charRating.cat === 'Sustain' && (
                              <h6 className="hsr-small-role sustain">
                                <FontAwesomeIcon
                                  icon={faBriefcaseMedical}
                                  width="12"
                                />{' '}
                                Sustain
                              </h6>
                            )}
                            <div className="bottom rev three">
                              <RatingBox
                                game="hsr"
                                rating={charRating.moc_rating}
                                ratingName="MoC (E0S0)"
                              />
                              <RatingBox
                                game="hsr"
                                rating={charRating.pure_rating}
                                ratingName="PF (E0S0)"
                              />
                              <RatingBox
                                game="hsr"
                                rating={charRating.apo_rating}
                                ratingName="AS (E0S0)"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="hsr-role">
                              This character belongs to the{' '}
                              {charRating.cat === 'DPS' && (
                                <span className="role dps">
                                  <FontAwesomeIcon
                                    icon={faHandFist}
                                    width="12"
                                  />{' '}
                                  DPS
                                </span>
                              )}
                              {charRating.cat === 'Specialist' && (
                                <span className="role debuffer">
                                  <FontAwesomeIcon icon={faVirus} width="12" />{' '}
                                  Support DPS
                                </span>
                              )}
                              {charRating.cat === 'Amplifier' && (
                                <span className="role support">
                                  <FontAwesomeIcon icon={faFlask} width="12" />{' '}
                                  Amplifier
                                </span>
                              )}
                              {charRating.cat === 'Sustain' && (
                                <span className="role sustain">
                                  <FontAwesomeIcon
                                    icon={faBriefcaseMedical}
                                    width="12"
                                  />{' '}
                                  Sustain
                                </span>
                              )}{' '}
                              role and the ratings rate their performance at
                              this role only.
                            </p>{' '}
                            <div className="bottom rev three">
                              <RatingBox
                                game="hsr"
                                rating={charRating.moc_rating}
                                ratingName="MoC (E0S0)"
                              />
                              <RatingBox
                                game="hsr"
                                rating={charRating.pure_rating}
                                ratingName="PF (E0S0)"
                              />
                              <RatingBox
                                game="hsr"
                                rating={charRating.apo_rating}
                                ratingName="AS (E0S0)"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="bottom rev three with-margin">
                      <RatingBox
                        game="hsr"
                        rating={charRating.moc_special_rating}
                        ratingName="MoC (E0)"
                      />
                      <RatingBox
                        game="hsr"
                        rating={charRating.pure_special_rating}
                        ratingName="PF (E0)"
                      />
                      <RatingBox
                        game="hsr"
                        rating={charRating.apo_special_rating}
                        ratingName="AS (E0)"
                      />
                    </div>
                    <div className="bottom rev three">
                      <RatingBox
                        game="hsr"
                        rating={charRating.moc_rating}
                        ratingName="MoC (E6)"
                      />
                      <RatingBox
                        game="hsr"
                        rating={charRating.pure_rating}
                        ratingName="PF (E6)"
                      />
                      <RatingBox
                        game="hsr"
                        rating={charRating.apo_rating}
                        ratingName="AS (E6)"
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      ) : (
        <Alert variant="primary">
          <p>
            This character ratings aren't available because they're not released
            yet.
          </p>
        </Alert>
      )}
    </div>
  );
};
